import { useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronGrabberVertical } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { todayUtc } from "@acto/web-common/dates";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { DatePicker } from "@acto/taskpane/field-sales-2/components/primitives/DatePicker";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { collapseTransition } from "@acto/taskpane/field-sales-2/styles/collapseTransition";
import { earliestCustomDay, type DurationChoice } from "@acto/taskpane/field-sales-2/utils/productHide";
import { hideDurationLabel } from "./hideDurationLabel";

type OptionKind = DurationChoice["kind"];

const OPTIONS: readonly OptionKind[] = ["once", "forever", "until"];

const Frame = styled("div")<{ expanded: boolean }>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
  justifyContent: "center",
  minHeight: theme.fieldSalesTwo.spacing[48],
  paddingBlock: theme.fieldSalesTwo.spacing[4],
  paddingInline: theme.fieldSalesTwo.spacing[20],
  borderRadius: theme.fieldSalesTwo.radius["38"],
  background: theme.fieldSalesTwo.colors.surface.glassMedium,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  transition: collapseTransition(theme, "padding"),
  variants: [{ props: { expanded: true }, style: { paddingBlock: theme.fieldSalesTwo.spacing[24] } }],
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

const SummaryRow = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  flexGrow: 1,
  marginBlock: `-${theme.fieldSalesTwo.spacing[4]}`,
  paddingBlock: theme.fieldSalesTwo.spacing[4],
  paddingInline: 0,
  border: "none",
  background: "transparent",
  appearance: "none",
  WebkitAppearance: "none",
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: theme.fieldSalesTwo.spacing[2],
    borderRadius: theme.fieldSalesTwo.radius["16"],
  },
}));

const Value = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "& svg": { width: "12px", height: "auto" },
}));

const CalendarPad = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[16],
}));

interface DurationFieldProps {
  value: DurationChoice;
  onChange: (value: DurationChoice) => void;
  today?: Date;
}

export function DurationField({ value, onChange, today = todayUtc() }: DurationFieldProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const { locale } = useLocale();
  const [openCalendar, setOpenCalendar] = useState(false);

  const optionLabel = (kind: OptionKind) =>
    ({ once: t("durationOnce"), forever: t("durationForever"), until: t("durationCustom") })[kind];

  const summaryLabel =
    value.kind === "until" && value.day
      ? hideDurationLabel({ kind: "until", lastDay: value.day }, t, locale)
      : optionLabel(value.kind);

  // Re-picking the current option must not clear an already chosen custom day.
  const selectOption = (kind: OptionKind) => {
    setOpenCalendar(kind === "until");
    if (kind === value.kind) return;
    onChange(kind === "until" ? { kind, day: null } : { kind });
  };

  const selectDay = (day: Date) => {
    onChange({ kind: "until", day });
    setOpenCalendar(false);
  };

  return (
    <Frame expanded={openCalendar}>
      <Collapse open={openCalendar}>
        <CalendarPad>
          <DatePicker
            value={value.kind === "until" ? value.day : null}
            onChange={selectDay}
            minDate={earliestCustomDay(today)}
          />
          <Divider />
        </CalendarPad>
      </Collapse>
      <MenuPopover
        trigger={
          <SummaryRow type="button">
            {t("durationLabel")}
            <Value>
              {summaryLabel}
              <ChevronGrabberVertical aria-hidden="true" />
            </Value>
          </SummaryRow>
        }
        ariaLabel={t("durationLabel")}
        align="end"
        selectedCheckTone="accent"
        items={OPTIONS.map((kind) => ({
          label: optionLabel(kind),
          selected: kind === value.kind,
          onSelect: () => selectOption(kind),
        }))}
      />
    </Frame>
  );
}
